import type { FC } from 'react';
import { useRef, useState, memo, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useOverTimeout } from 'app/hooks/useTimeout';
import ThemeContext from 'app/common/ThemeContext';
import { cssVariable } from 'app/utils/style';
import { getThumbnailUrl } from 'app/utils/image';
import { getCDNUrl } from 'app/utils/api_url';

import style from './style.module.scss';

interface Props extends ClassNameProp {
  team: OpponentBase | undefined;
  size?: number;
  withBackground?: boolean;
  avatarClassName?: string;
  noPlaceholder?: boolean;
  noAlt?: boolean;
}

const TeamAvatar: FC<Props> = ({
  team,
  size,
  withBackground,
  className,
  avatarClassName,
  noPlaceholder = false,
  noAlt = false
}) => {
  const { t } = useTranslation();
  const image = useRef<HTMLImageElement | null>(null);
  const [status, setStatus] = useState<'loading' | 'succeed' | 'failed'>('loading');
  const loadOvertime = useOverTimeout(100);
  const handleSuccess = () => {
    setStatus('succeed');
  };
  const handleError = () => {
    setStatus('failed');
  };
  const {
    theme: { mode }
  } = useContext(ThemeContext);

  const tbdName = t('all-team_name_tbd');
  const acronymBase = team?.acronym ?? team?.name;
  const acronym = acronymBase ? acronymBase.slice(0, 1) : tbdName;

  const containerClassName = classNames(style.teamAvatar, className, {
    [style.withBackground]: withBackground
  });

  const imageClassName = classNames(
    style.image,
    {
      [style.image_loading]: loadOvertime && status === 'loading'
    },
    avatarClassName
  );

  const rawImage =
    mode === 'dark' && team?.dark_mode_image_url ? team?.dark_mode_image_url : team?.image_url;
  const imageForTheme = getThumbnailUrl(rawImage ? getCDNUrl(rawImage) : rawImage);

  return (
    <span
      className={containerClassName}
      style={size ? cssVariable('size', `${size}px`) : undefined}
    >
      {team && imageForTheme && status !== 'failed' ? (
        <img
          ref={image}
          className={imageClassName}
          src={imageForTheme}
          alt={!noAlt ? team.name : ''}
          onLoad={handleSuccess}
          onError={handleError}
        />
      ) : (
        <>
          {!noPlaceholder && (
            <span className={classNames(style.avatar, avatarClassName)}>{acronym}</span>
          )}
        </>
      )}
    </span>
  );
};

TeamAvatar.displayName = 'TeamAvatar';

export default memo(TeamAvatar);
